import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiService from '../services/apiService';
import { useFasesDosVendedores, FaseBadge } from '../components/FaseBadge';
import './RevisaoPontos.css';

// Rótulos amigáveis das tabelas filhas da transcription (modal "Dados").
// Avaliação de vendas (= revisão de pontos), Markup e Auditoria de dados são omitidos de propósito.
const CHILD_LABELS = {
    transcriptions_classification: 'Classificação',
    transcriptions_feedback: 'Feedback',
    transcriptions_summary: 'Resumo',
    transcriptions_products: 'Produtos',
    transcriptions_objections: 'Objeções',
    transcriptions_lost_sales_reasons: 'Motivos de perda de venda',
    transcriptions_conversion: 'Conversão',
};

// Renderiza o conteúdo de cada filha no modal "Dados".
// Feedback -> textos GOOD/BAD; Resumo -> texto do campo data; demais -> JSON legível.
function renderChildContent(child, val) {
    const inner = (val && typeof val === 'object' && 'data' in val) ? val.data : val;
    if (child === 'transcriptions_feedback') {
        const fb = inner && typeof inner === 'object' ? (inner.feedback ?? inner) : inner;
        if (fb && typeof fb === 'object' && ('good' in fb || 'bad' in fb)) {
            return (
                <div className="rp-data-feedback">
                    <div className="rp-data-fb-block rp-data-fb-good">
                        <strong>👍 Pontos positivos</strong>
                        <p>{fb.good ? String(fb.good).trim() : '—'}</p>
                    </div>
                    <div className="rp-data-fb-block rp-data-fb-bad">
                        <strong>👎 Pontos a melhorar</strong>
                        <p>{fb.bad ? String(fb.bad).trim() : '—'}</p>
                    </div>
                </div>
            );
        }
        return <p className="rp-data-text">{typeof fb === 'string' ? fb : JSON.stringify(fb, null, 2)}</p>;
    }
    if (child === 'transcriptions_summary') {
        const txt = (inner && typeof inner === 'object') ? (inner.data ?? inner.summary ?? '') : inner;
        return <p className="rp-data-text">{txt ? String(txt).trim() : '—'}</p>;
    }
    if (child === 'transcriptions_classification') {
        if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
            const labels = {
                id: 'ID', classification: 'Classificação', original_classification: 'Classificação original',
                generate_feedback: 'Gera feedback', generate_score: 'Gera nota', reason: 'Motivo',
                confidence: 'Confiança',
            };
            const fmt = (v) => {
                if (v === true) return 'Sim';
                if (v === false) return 'Não';
                if (v === null || v === undefined || v === '') return '—';
                return (typeof v === 'object') ? JSON.stringify(v) : String(v);
            };
            return (
                <div className="rp-data-fields">
                    {Object.entries(inner).map(([k, v]) => (
                        <div className="rp-data-field" key={k}>
                            <span className="rp-data-field-label">{labels[k] || k}</span>
                            <span className="rp-data-field-value">{fmt(v)}</span>
                        </div>
                    ))}
                </div>
            );
        }
        return <p className="rp-data-text">{inner ? String(inner) : '—'}</p>;
    }
    return <pre className="rp-data-json">{JSON.stringify(inner, null, 2)}</pre>;
}

// Seletor de período por índice (mesmo padrão das outras telas, ex.: Home).
const fmtBR = (d) => `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
const buildAllDates = () => {
    const now = new Date();
    const cur = new Date(now);
    cur.setDate(cur.getDate() - 182);
    const dates = [];
    while (cur <= now) {
        dates.push(fmtBR(cur));
        cur.setDate(cur.getDate() + 1);
    }
    return dates;
};
const ALL_DATES = buildAllDates();
const TODAY_IDX = ALL_DATES.length - 1;
const idxToISO = (idx) => {
    const d = ALL_DATES[idx];
    if (!d) return '';
    const [dd, mm, yyyy] = d.split('/').map(Number);
    return `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
};
// Exibição em pt-BR: 'YYYY-MM-DD[...]' -> 'dd/mm/yyyy'
const brDate = (iso) => {
    if (!iso) return '—';
    const p = String(iso).split('T')[0].split('-');
    return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
};
// Origem da linha (campo `origin` do /points-review/attendances): o recorte de atendimento (AWON ou pipeline
// padrão) e a análise de comportamento do worker2 do AWON (só pontos) são registros separados.
const ORIGENS = {
    padrao_recorte: { label: 'Recorte', sub: 'pipeline padrão', cls: 'rp-origem-padrao' },
    awon_recorte: { label: 'Recorte', sub: 'AWON', cls: 'rp-origem-awon' },
    awon_comportamento: { label: 'Análise de comportamento', sub: 'AWON', cls: 'rp-origem-comport' },
};
const origemTag = (o) => {
    const x = ORIGENS[o];
    if (!x) return '—';
    return <span className={`rp-origem ${x.cls}`}>{x.label} <small>· {x.sub}</small></span>;
};

// recorte do AWON: segundos -> m:ss; window_start/end do AWON já vêm no horário local (sem fuso) -> HH:MM
const mmss = (v) => {
    const n = Math.max(0, Math.round(Number(v) || 0));
    return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
};
const horaLocal = (iso) => String(iso || '').slice(11, 16);

// created vem em UTC (naive) -> exibe dd/mm/yyyy HH:MM no fuso de São Paulo
const brDateTime = (iso) => {
    if (!iso) return '—';
    const s = String(iso);
    const dt = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : s + 'Z');
    if (isNaN(dt)) return brDate(iso);
    return dt.toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo',
    });
};
const PRESETS = [
    { label: 'Últimos 7 dias', s: Math.max(0, TODAY_IDX - 6), e: TODAY_IDX },
    { label: 'Últimos 15 dias', s: Math.max(0, TODAY_IDX - 14), e: TODAY_IDX },
    { label: 'Últimos 30 dias', s: Math.max(0, TODAY_IDX - 29), e: TODAY_IDX },
    { label: 'Últimos 90 dias', s: Math.max(0, TODAY_IDX - 89), e: TODAY_IDX },
];

const num = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 2 }));
// Pontos são inteiros (arredonda p/ exibição).
const intNum = (v) => (v === null || v === undefined ? '—' : Math.round(Number(v)).toLocaleString('pt-BR'));
// Soma dos pontos de comportamentos (substitui o antigo by_questions escalar).
const sumBeh = (arr) => (arr || []).reduce((acc, b) => acc + (Number(b.points) || 0), 0);

// Reconstrói o valor efetivo atual de um item a partir do original + última revisão.
const pick = (rev, key, orig) => (rev && rev[key] !== null && rev[key] !== undefined ? rev[key] : orig);

function currentValues(detail, rev) {
    const orig = detail || {};
    const origScore = orig.score_prompt ? orig.score_prompt.score : null;
    const origEvidence = orig.score_prompt ? orig.score_prompt.evidence : null;
    const cond = pick(rev, 'new_condition', orig.condition);
    const score = cond === false ? null : pick(rev, 'new_score', origScore);
    const evidence = pick(rev, 'new_evidence', origEvidence);
    const earn = pick(rev, 'new_earn_points', orig.earn_points);
    const points = pick(rev, 'new_points', orig.points);
    return { cond, score, evidence, earn, points };
}

/**
 * Pontos que o vendedor fez DENTRO de uma fase e que **não entram** no total da linha.
 *
 * Por que a linha fica zerada e a expansão mostra atendimentos com ponto: fase com régua de PREÇO
 * própria tem pontos incomparáveis com os do ongoing, então eles saem das somas (§N22) — mas a
 * LISTA de atendimentos continua inteira, porque o vendedor tem de ver o que fez. Sem esta nota, a
 * tela mostra 0 para quem pontuou 31 e ninguém consegue explicar a diferença.
 */
/** Números EFETIVOS do vendedor no período: ongoing + as fases que não somam no ongoing.
 *
 * POR QUE somar: uma fase com régua de preço própria tira seus atendimentos da soma do ongoing
 * (§N21) para não misturar réguas no RANKING. Só que esta tela não é o ranking — é a revisão, e
 * quem olha quer saber quantos atendimentos válidos o vendedor teve, ponto. Enquanto as colunas
 * mostravam só o ongoing, todo vendedor em fase com régua própria aparecia ZERADO: no leroymerlin
 * isso é a turma inteira até 03/09.
 *
 * `fonte` diz de onde veio, para a tela poder marcar visualmente a linha que mistura as duas
 * réguas — o alerta do §N21 continua válido, ele só não justifica esconder o número.
 *
 * Hora de gravação fica de fora da soma de propósito: a fase não a reporta (`awon_chunks` não tem
 * `transcription_id`, então tempo gravado não é atribuível a uma fase). */
function efetivo(s) {
    const fora = (s.by_phase || []).filter((f) => f.counts_in_ongoing === false);
    const soma = (base, chave) => {
        const o = s[chave] || {};
        const acc = fora.reduce((a, f) => {
            const v = f[chave] || {};
            return { count: a.count + (v.count || 0), points: a.points + (v.points || 0) };
        }, { count: 0, points: 0 });
        return {
            count: (o.count || 0) + acc.count,
            points: (o.points || 0) + acc.points,
            daFase: acc,
            temFase: fora.length > 0 && (acc.count > 0 || acc.points > 0),
        };
    };
    const behFase = fora.reduce((a, f) => a + (f.by_behavior || []).reduce((x, b) => x + (b.points || 0), 0), 0);
    return {
        validos: soma(s, 'by_valid_attendances'),
        invalidos: soma(s, 'by_invalid_attendances'),
        comportamento: behFase,
        total: (s.total_points || 0) + fora.reduce((a, f) => a + (f.total_points || 0), 0),
        fases: fora,
        temFase: fora.length > 0,
    };
}

/** Comportamentos que o vendedor marcou ao dar dislike. Não há coluna para isso: o app grava
 * no próprio `feedbacks.comments`, no formato "🤚 Discordo da pontuação. Marquei: k1,k2. <texto>",
 * com as mesmas chaves de `questions.behavior_key`. "Marquei: . " = nenhum marcado. */
function comportamentosDoDislike(comments) {
    const m = /Marquei:\s*([^.]*)\./.exec(String(comments || ''));
    return m ? m[1].split(',').map((s) => s.trim()).filter(Boolean) : [];
}

/** Ordem das perguntas na revisão (pedido de 27/10): primeiro as que têm comportamento associado, depois as
 * sem comportamento, em ordem alfabética do texto da pergunta dentro de cada grupo. Num dislike com
 * comportamentos marcados, as perguntas desses comportamentos vêm antes de tudo (na ordem em que o vendedor
 * marcou) — é o que o revisor precisa olhar primeiro. */
function ordenarPerguntas(items, marcados) {
    const rank = (it) => {
        const i = it.behavior_key ? marcados.indexOf(it.behavior_key) : -1;
        if (i >= 0) return i;
        return it.behavior_key ? marcados.length : marcados.length + 1;
    };
    return items.map((it) => ({ it, r: rank(it) }))
        .sort((a, b) => a.r - b.r || String(a.it.question || '').localeCompare(String(b.it.question || ''), 'pt-BR',
            { sensitivity: 'base' }))
        .map((x) => x.it);
}

/** Dica do "Pontos" do atendimento: de onde vem a soma, item a item.
 *
 * A API anula `behavior_key` da pergunta cujo comportamento NÃO está no vocabulário da loja
 * (points_review_routes ~1597). É esse o sinal que permite marcar aqui as pontuações que o
 * atendimento conta mas que o agregado do vendedor descarta — a origem da divergência entre as
 * duas telas. Sem essa marcação o usuário vê 13 aqui, 10 lá, e nada explica a diferença. */
function dicaPontosAtendimento(a) {
    const qs = (a.questions || []).filter((q) => (q.earned_points || 0) > 0);
    const linhas = qs.map((q) => {
        const nome = q.behavior_display_name || q.question || `item ${q.item}`;
        const orfa = !q.behavior_key ? '  ⚠ fora do vocabulário da loja' : '';
        return `  ${nome}: ${intNum(q.earned_points)}${orfa}`;
    });
    const somaQ = qs.reduce((acc, q) => acc + (q.earned_points || 0), 0);
    const orfas = qs.filter((q) => !q.behavior_key);
    const somaOrfas = orfas.reduce((acc, q) => acc + (q.earned_points || 0), 0);
    const partes = [
        `Perguntas que pontuaram (${qs.length}): ${intNum(somaQ)}`,
        ...linhas,
    ];
    if ((a.valid_attendance_points || 0) > 0) partes.push(`Atendimento válido: ${intNum(a.valid_attendance_points)}`);
    if ((a.invalid_attendance_points || 0) > 0) partes.push(`Atendimento não-válido: ${intNum(a.invalid_attendance_points)}`);
    partes.push('', `TOTAL DESTE ATENDIMENTO: ${intNum(a.earned_points)}`);
    if (orfas.length) {
        partes.push('',
            `⚠ ${intNum(somaOrfas)} ponto(s) de ${orfas.length} comportamento(s) NÃO cadastrado(s) no`,
            'vocabulário desta loja. Contam aqui, mas somem do total do vendedor.');
    }
    return partes.join('\n');
}

/** Dica do "Comportamentos" do vendedor: quem pontuou e de que parcela veio.
 *
 * O agregado percorre o VOCABULÁRIO da loja e busca a pontuação de cada chave
 * (points_review_routes ~728, `for bk in registered`). Comportamento pontuado cuja chave não está
 * no vocabulário não é procurado — e some, sem erro. Por isso a dica diz explicitamente de onde a
 * soma vem: é a única forma de o usuário entender por que este número é menor que o do
 * atendimento. */
function dicaComportamentos(s, ef) {
    const ongoing = (s.by_behavior || []).filter((b) => (b.points || 0) > 0);
    const partes = [];
    if (ongoing.length) {
        partes.push(`Ongoing (${intNum(ongoing.reduce((a, b) => a + b.points, 0))}):`);
        ongoing.forEach((b) => partes.push(`  ${b.display_name || b.behavior_key}: ${intNum(b.points)}`));
    }
    (ef.fases || []).forEach((f) => {
        const bs = (f.by_behavior || []).filter((b) => (b.points || 0) > 0);
        partes.push(`${partes.length ? '\n' : ''}Fase ${f.phase_name} (${intNum(bs.reduce((a, b) => a + b.points, 0))}):`);
        bs.forEach((b) => partes.push(`  ${b.display_name || b.behavior_key}: ${intNum(b.points)}`));
    });
    if (!partes.length) partes.push('Nenhum comportamento pontuou no período.');
    partes.push('', 'Soma apenas os comportamentos CADASTRADOS no vocabulário desta loja.',
        'Comportamento pontuado pela IA mas não cadastrado não entra aqui — e por isso este',
        'número pode ser MENOR que a soma dos atendimentos. Confira a coluna Pontos de cada',
        'atendimento: os itens marcados com ⚠ são os que ficam de fora.');
    return partes.join('\n');
}

function PontosForaDoPlacar({ byPhase }) {
    const fora = (byPhase || []).filter((f) => f.counts_in_ongoing === false && f.total_points);
    if (fora.length === 0) return null;
    const total = fora.reduce((acc, f) => acc + (f.total_points || 0), 0);
    const titulo = fora.map((f) => {
        const beh = (f.by_behavior || []).reduce((a, b) => a + (b.points || 0), 0);
        return [
            `${f.phase_name}: ${Math.round(f.total_points)} pts`,
            `  ${f.by_valid_attendances.count} atend. válidos × ${f.by_valid_attendances.points_per_unit ?? '—'} = ${Math.round(f.by_valid_attendances.points)}`,
            beh ? `  comportamento: ${Math.round(beh)}` : null,
        ].filter(Boolean).join('\n');
    }).join('\n') + '\n\nEsta fase tem régua de pontuação própria, então os pontos dela não somam '
      + 'no total do ongoing — seriam incomparáveis com os dos outros vendedores.';
    return (
        <div className="rp-fora-placar" title={titulo}>
            🎯 {Math.round(total)} pts na fase <span>(fora do total)</span>
        </div>
    );
}


// AWON (03/11): as falas da vendedora-principal e da cliente-principal em destaque. A linha de quem fala é
// "00m10s Nome (vendedor-principal): ..."; as linhas seguintes sem rótulo continuam a mesma fala. Transcrição sem esses
// papéis (pipeline padrão) sai como antes.
const RP_FALA_RE = /^\s*\d+m\d+s\s+[^:\n]{1,80}:/;
const RP_PAPEL_RE = /^\s*(?:\d+m\d+s\s+)?[^:\n]{0,80}\((vendedor-principal|cliente-principal)\)\s*:/;
// AWON (03/11): transcrição original × resultante do julgador, só para ver. Uma caixa, em ordem de horário: o que
// ficou em texto normal, o que saiu em cinza claro (legível) e o que o julgador acrescentou com "+"; a vendedora- e a
// cliente-principal em destaque no que ficou. Nada disso vai ao n8n.
const RP_JULGADOR_FEZ = { confirmar: 'confirmou o recorte', ajustar: 'ajustou as bordas', juntar: 'juntou recortes',
    dividir: 'dividiu o recorte', criar: 'criou o atendimento (sem recorte)' };
const rpHora = (iso) => (iso ? String(iso).slice(11, 19) : '—');
function ComparacaoJulgador({ dados }) {
    if (dados.carregando) return <p className="rp-empty">Carregando a comparação…</p>;
    if (dados.erro) return <p className="rp-empty">{dados.erro}</p>;
    if (dados.restricted_admin) return <p className="rp-empty">Sem acesso à transcrição deste atendimento.</p>;
    const c = dados.comando || {};
    const k = dados.contagem || {};
    const quem = c.por_pessoa ? 'Uma pessoa' : 'O julgador';
    return (
        <div className="rp-cmp">
            <div className="rp-subtitle rp-cmp-titulo">Transcrição original × resultante do julgador</div>
            <div className="rp-cmp-cab">
                {quem} {RP_JULGADOR_FEZ[c.command] || c.command}
                {c.round_slot ? ` na rodada das ${String(c.round_slot).slice(11, 16)}` : ''}.
                {' '}Original: {(dados.originais || []).length
                    ? dados.originais.map((o) => `#${o.session_id} ${rpHora(o.inicio)}–${rpHora(o.fim)}`).join(', ')
                    : 'sem recorte'}
                {' '}→ resultante: #{dados.resultado?.session_id} {rpHora(dados.resultado?.inicio)}–{rpHora(dados.resultado?.fim)}.
                {dados.mudou
                    ? <> Ficou {k.ficou || 0} · saiu {k.saiu || 0} · entrou {k.entrou || 0} falas.</>
                    : <> O julgador não mudou o recorte.</>}
                <div className="rp-cmp-legenda">
                    Em cinza claro, o que saiu; com <span className="rp-cmp-mais">+</span>, o que o julgador acrescentou.
                    Só para consulta: o n8n recebe só a transcrição do recorte.
                </div>
            </div>
            {(dados.falas || []).length ? (
                <div className="rp-transcript rp-transcript-papeis rp-cmp-falas">
                    {dados.falas.map((f, i) => {
                        const papel = f.estado !== 'saiu' && ['vendedor-principal', 'cliente-principal'].includes(f.papel)
                            ? ` rp-fala-${f.papel}` : '';
                        return (
                            <div key={i} className={`rp-fala rp-cmp-${f.estado}${papel}`}>
                                <span className="rp-cmp-marca">{f.estado === 'entrou' ? '+' : ''}</span>
                                {f.hora} {f.quem}{['vendedor-principal', 'cliente-principal'].includes(f.papel) ? ` (${f.papel})` : ''}: {f.texto}
                            </div>
                        );
                    })}
                </div>
            ) : <p className="rp-empty">Sem fala transcrita no trecho.</p>}
        </div>
    );
}

function TranscricaoDestacada({ texto }) {
    let papel = null;
    const linhas = String(texto || '').split('\n').map((l) => {
        if (RP_FALA_RE.test(l) || RP_PAPEL_RE.test(l)) {
            const m = l.match(RP_PAPEL_RE);
            papel = m ? m[1] : null;
        }
        return [l, papel];
    });
    if (!linhas.some(([, p]) => p)) return <pre className="rp-transcript">{texto}</pre>;
    return (
        <pre className="rp-transcript rp-transcript-papeis">
            {linhas.map(([l, p], i) => (
                <span key={i} className={p ? `rp-fala rp-fala-${p}` : 'rp-fala'}>{l || ' '}</span>
            ))}
        </pre>
    );
}

function RevisaoPontos({ user }) {
    const userId = user?.user_id;
    const [toast, setToast] = useState(null);
    const notify = useCallback((msg, type = 'info') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 4000);
    }, []);

    // Filtros
    const [companies, setCompanies] = useState([]);
    const [stores, setStores] = useState([]);
    const [subdomain, setSubdomain] = useState('');
    // Fases de TODOS os vendedores da empresa numa chamada: a tabela lista muitos, e uma chamada
    // por linha cresceria com o cliente (§9.5).
    const { fases: fasesVend } = useFasesDosVendedores({ subdomain });
    const [storeId, setStoreId] = useState('');
    const [salesmanId, setSalesmanId] = useState('');
    const [salesmen, setSalesmen] = useState([]);
    const [startIdx, setStartIdx] = useState(Math.max(0, TODAY_IDX - 14));   // default: últimos 15 dias
    const [endIdx, setEndIdx] = useState(TODAY_IDX);

    // Dados
    const [results, setResults] = useState(null);
    const [attendances, setAttendances] = useState([]);
    const [loading, setLoading] = useState(false);

    // Config

    // Detalhe do atendimento
    const [detail, setDetail] = useState(null);            // { transcription_id, evaluation_id, result, effective_by_item, effective_metrics }
    const [drafts, setDrafts] = useState({});              // item -> { cond, score, evidence, justification }
    const [abertas, setAbertas] = useState({});            // {item: true} da ÚNICA pergunta expandida (padrão: todas recolhidas)
    const [transcript, setTranscript] = useState(null);
    const [comparacao, setComparacao] = useState(null);   // AWON: original × resultante do julgador
    const [contextTranscript, setContextTranscript] = useState(null);
    const [contextSpans, setContextSpans] = useState([]);
    // Admin limitado (core.admin_companies): só a versão com contexto anonimizado e só o áudio
    // anonimizado. Default restrito até a API responder — a API é quem decide de fato.
    const [restrictedAdmin, setRestrictedAdmin] = useState(true);
    const [audioUrl, setAudioUrl] = useState(null);
    const [savingReview, setSavingReview] = useState(false);
    const [openingId, setOpeningId] = useState(null);
    const [openSalesman, setOpenSalesman] = useState(null);  // "store_id-salesman_id" do vendedor expandido
    const [goalsByStore, setGoalsByStore] = useState({});    // store_id -> loja do /goals (metas por loja)

    // Atendimento virtual
    const [virtualModal, setVirtualModal] = useState(null);  // { s } do vendedor selecionado
    const [vaClassifications, setVaClassifications] = useState([]);
    const [vaForm, setVaForm] = useState({ date_start: '', date_end: '', classification_id: '' });
    const [vaSaving, setVaSaving] = useState(false);

    // Modal "Dados" (extrações das filhas) + marca de revisão
    const [dataModal, setDataModal] = useState(null);        // { transcription_id, data } | null
    const [dataLoading, setDataLoading] = useState(false);
    const [marking, setMarking] = useState(false);
    const [confirmModal, setConfirmModal] = useState(null);  // { message, onConfirm } | null
    const [fbAnswer, setFbAnswer] = useState('');            // resposta ao feedback (comments_answer)
    const [fbSaving, setFbSaving] = useState(false);

    // Deep-link da Home (alerta de dislike): pré-seleciona empresa/loja/vendedor e filtra por feedback_ids
    const [searchParams] = useSearchParams();
    const deepLinkRef = useRef(null);
    const [feedbackFilter, setFeedbackFilter] = useState(null);  // Set<number> | null
    const [autoExpandKey, setAutoExpandKey] = useState(null);    // "store-salesman" p/ abrir sozinho
    const [reviewedFilter, setReviewedFilter] = useState('all'); // all | reviewed | not_reviewed
    const [reviewerFilter, setReviewerFilter] = useState('');    // '' = todos; senão nome do revisor

    // Carregar empresas (admin)
    useEffect(() => {
        (async () => {
            try {
                const data = await apiService.promptEditorGetCompanies();
                setCompanies(Array.isArray(data) ? data : []);
            } catch (e) {
                notify('Erro ao carregar empresas', 'error');
            }
        })();
    }, [notify]);

    // Carregar lojas + config ao trocar empresa
    useEffect(() => {
        setStores([]); setStoreId(''); setSalesmanId(''); setSalesmen([]);
        setResults(null); setAttendances([]); setDetail(null);
        if (!subdomain) return;
        (async () => {
            try {
                const st = await apiService.promptEditorGetStores(subdomain);
                setStores(Array.isArray(st) ? st : []);
            } catch (e) { notify('Erro ao carregar lojas', 'error'); }
        })();
    }, [subdomain, userId, notify]);

    // Carregar vendedores ao escolher empresa/loja (independe da busca)
    useEffect(() => {
        if (!subdomain) { setSalesmen([]); return; }
        (async () => {
            try {
                const r = await apiService.pointsSalesmen({ userId, subdomain, store_id: storeId || undefined });
                setSalesmen(r.salesmen || []);
            } catch (e) { /* silencioso */ }
        })();
    }, [subdomain, storeId, userId]);

    const buscar = useCallback(async () => {
        if (!subdomain) { notify('Selecione uma empresa', 'error'); return; }
        const ds = idxToISO(startIdx), de = idxToISO(endIdx);
        setLoading(true); setDetail(null); setOpenSalesman(null);
        const filt = {
            userId, subdomain,
            store_id: storeId || undefined,
            salesman_id: salesmanId || undefined,
            date_start: ds, date_end: de,
        };
        try {
            const [res, att, goalsResp] = await Promise.all([
                apiService.pointsResults(filt),
                apiService.pointsAttendances(filt),
                apiService.pointsGoals({ subdomain }).catch(() => null),
            ]);
            setResults(res);
            setAttendances(att.attendances || []);
            const gmap = {};
            (goalsResp?.stores || []).forEach((st) => { gmap[st.store_id] = st; });
            setGoalsByStore(gmap);
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao buscar dados', 'error');
        } finally {
            setLoading(false);
        }
    }, [userId, subdomain, storeId, salesmanId, startIdx, endIdx, notify]);

    // Deep-link (Home → dislike): lê params 1x, alarga o período e seleciona a empresa.
    useEffect(() => {
        const sub = searchParams.get('subdomain');
        if (!sub) return;
        const fbRaw = searchParams.get('feedback_ids') || '';
        const fbSet = fbRaw
            ? new Set(fbRaw.split(',').map((x) => Number(x.trim())).filter((n) => !Number.isNaN(n) && n > 0))
            : null;
        deepLinkRef.current = {
            store_id: searchParams.get('store_id') || '',
            salesman_id: searchParams.get('salesman_id') || '',
            // atendimento a abrir já expandido (link do julgador no AWON Monitor)
            transcription_id: Number(searchParams.get('transcription_id')) || null,
            applied: false,
        };
        setFeedbackFilter(fbSet && fbSet.size ? fbSet : null);
        // date=AAAA-MM-DD (dia do atendimento): busca só aquele dia; sem ela, a janela completa p/ pegar
        // atendimentos antigos do alerta
        const dia = /^(\d{4})-(\d{2})-(\d{2})$/.exec(searchParams.get('date') || '');
        const diaIdx = dia ? ALL_DATES.indexOf(`${dia[3]}/${dia[2]}/${dia[1]}`) : -1;
        setStartIdx(diaIdx >= 0 ? diaIdx : 0);
        setEndIdx(diaIdx >= 0 ? diaIdx : TODAY_IDX);
        setSubdomain(sub);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Aplica a loja do deep-link quando as lojas carregam.
    useEffect(() => {
        const dl = deepLinkRef.current;
        if (!dl || dl.applied || !dl.store_id) return;
        if (stores.some((s) => String(s.id) === String(dl.store_id)) && String(storeId) !== String(dl.store_id)) {
            setStoreId(String(dl.store_id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stores]);

    // Aplica o vendedor do deep-link quando os vendedores carregam.
    useEffect(() => {
        const dl = deepLinkRef.current;
        if (!dl || dl.applied || !dl.salesman_id) return;
        if (salesmen.some((s) => String(s.salesman_id) === String(dl.salesman_id)) && String(salesmanId) !== String(dl.salesman_id)) {
            setSalesmanId(String(dl.salesman_id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salesmen]);

    // Dispara a busca 1x quando empresa/loja/vendedor do deep-link já estão selecionados.
    useEffect(() => {
        const dl = deepLinkRef.current;
        if (!dl || dl.applied || !subdomain) return;
        const storeOk = !dl.store_id || String(storeId) === String(dl.store_id);
        const smOk = !dl.salesman_id || String(salesmanId) === String(dl.salesman_id);
        if (storeOk && smOk) {
            dl.applied = true;
            if (dl.store_id && dl.salesman_id) setAutoExpandKey(`${dl.store_id}-${dl.salesman_id}`);
            buscar();
        }
    }, [subdomain, storeId, salesmanId, buscar]);

    // Auto-expande o vendedor do deep-link assim que os resultados chegam.
    useEffect(() => {
        if (autoExpandKey && results) {
            setOpenSalesman(autoExpandKey);
            setAutoExpandKey(null);
        }
    }, [results, autoExpandKey]);

    // Deep-link com transcription_id: abre o vendedor e o atendimento já expandido, e rola até ele.
    useEffect(() => {
        const dl = deepLinkRef.current;
        if (!dl?.transcription_id || !dl.applied || loading || !results) return;
        const tid = dl.transcription_id;
        dl.transcription_id = null;
        const a = attendances.find((x) => x.transcription_id === tid);
        if (!a) {
            notify(`Atendimento #${tid} não encontrado no período`, 'error');
            return;
        }
        setOpenSalesman(`${a.store_id}-${a.salesman_id}`);
        openAttendance(a);
        setTimeout(() => document.getElementById(`rp-att-${tid}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attendances, results, loading]);

    const openVirtualModal = async (s) => {
        setVaForm({ date_start: '', date_end: '', classification_id: '' });
        setVirtualModal({ s });
        try {
            const r = await apiService.pointsClassifications({ subdomain });
            setVaClassifications(r.classifications || []);
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao carregar classificações', 'error');
        }
    };

    const closeVirtualModal = () => { setVirtualModal(null); setVaSaving(false); };

    const saveVirtualAttendance = async () => {
        if (!vaForm.date_start || !vaForm.date_end || !vaForm.classification_id) {
            notify('Preencha data de início, fim e classificação', 'error'); return;
        }
        if (vaForm.date_end < vaForm.date_start) {
            notify('A data de fim não pode ser anterior à de início', 'error'); return;
        }
        setVaSaving(true);
        try {
            const r = await apiService.pointsCreateVirtualAttendance({
                subdomain,
                salesman_id: virtualModal.s.salesman_id,
                classification_id: Number(vaForm.classification_id),
                date_start: vaForm.date_start,
                date_end: vaForm.date_end,
            });
            const asTxt = r.counts_as === 'valido' ? 'válido' : (r.counts_as === 'invalido' ? 'inválido' : 'sem efeito na contagem');
            notify(`Atendimento virtual criado (conta como ${asTxt})${r.warning ? ' · ' + r.warning : ''}`, 'success');
            closeVirtualModal();
            buscar();
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao criar atendimento virtual', 'error');
            setVaSaving(false);
        }
    };

    const openAttendance = async (a) => {
        setTranscript(null); setComparacao(null); setAudioUrl(null); setDetail(null); setDrafts({}); setAbertas({});
        // Atendimento sem avaliação de perguntas (ex.: inválido): abre painel mínimo, só p/
        // Transcrição/Áudio/Dados/Marcar revisão (o /attendance exige avaliação e daria 404).
        const fbInfo = {
            feedback_id: a.feedback_id,
            feedback_likes: a.feedback_likes,
            feedback_comments: a.feedback_comments,
            feedback_comments_answer: a.feedback_comments_answer,
        };
        setFbAnswer(a.feedback_comments_answer || '');
        if (!a.is_reviewable) {
            setDetail({
                transcription_id: a.transcription_id,
                has_audio: a.has_audio,
                is_reviewed: !!a.is_reviewed,
                reviewed_by: a.reviewed_by,
                result: null,
                effective_metrics: null,
                behaviors: {},
                ...fbInfo,
            });
            return;
        }
        setOpeningId(a.transcription_id);
        try {
            const d = await apiService.pointsAttendance({ userId, subdomain, transcription_id: a.transcription_id });
            setDetail({ ...d, has_audio: a.has_audio, is_reviewed: !!a.is_reviewed, reviewed_by: a.reviewed_by, ...fbInfo });
            // inicializa drafts a partir do efetivo atual
            const dd = {};
            (d.result?.data?.evaluation_details || []).forEach((item) => {
                const rev = d.effective_by_item?.[item.item];
                const cv = currentValues(item, rev);
                dd[item.item] = { ...cv, justification: '' };
            });
            setDrafts(dd);
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao abrir atendimento', 'error');
        } finally {
            setOpeningId(null);
        }
    };

    useEffect(() => {
        (async () => {
            const r = await apiService.adminScope();
            setRestrictedAdmin(!!r.data?.restricted);
        })();
    }, []);

    const loadTranscript = async () => {
        try {
            const t = await apiService.pointsTranscription({ userId, subdomain, transcription_id: detail.transcription_id });
            setRestrictedAdmin(!!t.restricted_admin);
            // admin limitado nao recebe anon_transcript da API; nao mostra a caixa vazia
            setTranscript(t.restricted_admin ? null : (t.anon_transcript || '(sem transcrição)'));
            setContextTranscript(t.anon_context_transcript || '(ainda não processada)');
            setContextSpans(Array.isArray(t.anon_context_spans) ? t.anon_context_spans : []);
        } catch (e) { notify('Erro ao carregar transcrição', 'error'); }
    };

    const loadComparacao = async () => {
        if (comparacao && !comparacao.erro) { setComparacao(null); return; }   // o botão abre e fecha
        setComparacao({ carregando: true });
        try {
            const r = await apiService.pointsAwonComparacao({ userId, subdomain, transcription_id: detail.transcription_id });
            setComparacao(r);
        } catch (e) {
            setComparacao({ erro: e.response?.data?.detail || 'Não foi possível comparar a transcrição' });
        }
    };

    const loadAudio = async (redacted = false) => {
        try {
            const r = await apiService.pointsAudio({ userId, subdomain, transcription_id: detail.transcription_id, redacted });
            setAudioUrl(r.presigned_url);
        } catch (e) { notify(e.response?.data?.detail || 'Áudio indisponível', 'error'); }
    };

    const saveFeedbackAnswer = async () => {
        if (!detail?.feedback_id) return;
        setFbSaving(true);
        try {
            await apiService.pointsFeedbackAnswer({ userId, subdomain, feedback_id: detail.feedback_id, comments_answer: fbAnswer });
            notify('Resposta salva', 'success');
            setDetail((prev) => (prev ? { ...prev, feedback_comments_answer: fbAnswer } : prev));
            setAttendances((prev) => prev.map((x) => (x.transcription_id === detail.transcription_id ? { ...x, feedback_comments_answer: fbAnswer } : x)));
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao salvar resposta', 'error');
        } finally {
            setFbSaving(false);
        }
    };

    const openDataModal = async (transcription_id, feedback_id) => {
        setDataModal({ transcription_id, feedback_id, data: null, models: {} });
        setDataLoading(true);
        try {
            const r = await apiService.pointsAttendanceData({ userId, subdomain, transcription_id });
            setDataModal({ transcription_id, feedback_id, data: r.data || {}, models: r.models || {} });
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao carregar dados do atendimento', 'error');
            setDataModal(null);
        } finally {
            setDataLoading(false);
        }
    };

    // Marca o atendimento (detalhe aberto) como revisado — atualiza a lista localmente (badge + read-only)
    // sem recarregar tudo (evita fechar o painel). Não afeta pontuação.
    const markReviewed = async () => {
        if (!detail || detail.is_reviewed) return;
        setMarking(true);
        try {
            await apiService.pointsMarkReview({ userId, subdomain, transcription_id: detail.transcription_id, reviewed: true });
            notify('Atendimento marcado como revisado', 'success');
            const firstName = (user?.fullname || '').trim().split(/\s+/)[0] || undefined;
            setDetail((prev) => (prev ? { ...prev, is_reviewed: true, reviewed_by: firstName } : prev));
            setAttendances((prev) => prev.map((x) => (x.transcription_id === detail.transcription_id ? { ...x, is_reviewed: true, reviewed_by: firstName } : x)));
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao marcar revisão', 'error');
        } finally {
            setMarking(false);
        }
    };

    const baselineFor = (item) => {
        const rev = detail.effective_by_item?.[item.item];
        return currentValues(item, rev);
    };

    const setDraft = (itemNo, patch) => {
        setDrafts((prev) => ({ ...prev, [itemNo]: { ...prev[itemNo], ...patch } }));
    };

    const isModified = (item) => {
        const b = baselineFor(item);
        const d = drafts[item.item] || {};
        const norm = (x) => (x === '' || x === undefined ? null : x);
        return norm(b.cond) !== norm(d.cond)
            || Number(norm(b.score)) !== Number(norm(d.score))
            || norm(b.evidence) !== norm(d.evidence)
            || norm(b.earn) !== norm(d.earn)
            || Number(norm(b.points)) !== Number(norm(d.points));
    };

    const modifiedItems = (detail?.result?.data?.evaluation_details || []).filter(isModified);

    const saveReview = async () => {
        if (modifiedItems.length === 0) { notify('Nenhuma alteração para salvar', 'info'); return; }
        const items = modifiedItems.map((item) => {
            const d = drafts[item.item];
            return {
                item: item.item,
                condition: d.cond,
                score: d.cond === false ? null : (d.score === '' || d.score === null ? null : Number(d.score)),
                evidence: d.evidence,
                earn_points: d.earn === true,
                points: (d.points === '' || d.points === null || d.points === undefined) ? null : Number(d.points),
                justification: d.justification || null,
            };
        });
        setSavingReview(true);
        try {
            await apiService.pointsSaveReview({ userId, subdomain, transcription_id: detail.transcription_id, items });
            notify(`Revisão salva (${items.length} pergunta(s))`, 'success');
            // recarrega detalhe (novos overrides) + listas
            const a = attendances.find((x) => x.transcription_id === detail.transcription_id);
            await openAttendance(a || { transcription_id: detail.transcription_id, has_audio: detail.has_audio });
            buscar();
        } catch (e) {
            notify(e.response?.data?.detail || 'Erro ao salvar revisão', 'error');
        } finally {
            setSavingReview(false);
        }
    };

    const toggleRevisar = (a) => {
        if (detail?.transcription_id === a.transcription_id) { setDetail(null); return; }
        openAttendance(a);
    };

    const toggleSalesman = (key) => {
        setDetail(null);
        setOpenSalesman((prev) => (prev === key ? null : key));
    };

    // Meta da loja: janela cujo range cobre o dia atual (SP); senão a primeira. Inclui salesman_count (override).
    const todaySP = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }); // YYYY-MM-DD
    const goalInfo = (storeId) => {
        const st = goalsByStore[storeId];
        if (!st || !Array.isArray(st.goals) || st.goals.length === 0) return null;
        const g = st.goals.find((w) => w.date_start <= todaySP && todaySP <= w.date_end) || st.goals[0];
        return g ? { ...g, salesman_count: st.salesman_count } : null;
    };

    // De onde veio o atendimento no AWON: o recorte (#) e, se ele saiu da divisão de um recorte com atendimentos
    // emendados, o completo de origem e as outras partes. O link do Monitor (áudio e transcrição do completo) fica
    // escondido para o admin restrito, como o áudio original.
    const renderAwonOrigem = (a) => {
        if (!a?.awon_session_id) return null;
        const sp = a.awon_split;
        const linkMonitor = (id, txt) => (restrictedAdmin ? null : (
            <a className="rp-link" href={`/awon-monitor?subdomain=${encodeURIComponent(subdomain)}&session=${id}`}
                target="_blank" rel="noopener noreferrer">{txt}</a>
        ));
        if (!sp) {
            return (
                <div className="rp-awon-origem">
                    Recorte #{a.awon_session_id} do AWON {linkMonitor(a.awon_session_id, '· abrir no Monitor AWON ↗')}
                </div>
            );
        }
        const avisos = [sp.vendedor_inferido && 'vendedora inferida pelo juiz (voz não reconhecida)',
            sp.intercalado && 'intercalado com outro atendimento'].filter(Boolean);
        const irmao = (sib) => attendances.find((x) => x.transcription_id === sib.transcription_id
            && x.salesman_id === a.salesman_id);
        return (
            <div className="rp-awon-origem rp-awon-split">
                <div>
                    ✂ <strong>Parte {sp.index} de {sp.count}</strong> de um recorte com atendimentos emendados que a IA
                    dividiu — recorte completo #{sp.parent}
                    {sp.parent_window_start && <> ({horaLocal(sp.parent_window_start)}–{horaLocal(sp.parent_window_end)}, {mmss(sp.parent_duration)} min)</>}.
                    {' '}Esta parte é o trecho {mmss(sp.inicio_s)}–{mmss(sp.fim_s)} do completo.
                </div>
                {avisos.length > 0 && <div className="rp-awon-aviso">⚠ {avisos.join(' · ')}</div>}
                <div className="rp-awon-links">
                    {linkMonitor(sp.parent, '▶ Ouvir e ler o recorte completo no Monitor AWON ↗')}
                    {(sp.siblings || []).length > 0 && (
                        <span>
                            Outras partes:{' '}
                            {sp.siblings.map((sib, i) => {
                                const at = irmao(sib);
                                return (
                                    <React.Fragment key={sib.session_id}>
                                        {i > 0 && ' · '}
                                        {at ? (
                                            <button type="button" className="rp-link-btn" onClick={() => toggleRevisar(at)}
                                                title={`recorte #${sib.session_id}`}>parte {sib.index}</button>
                                        ) : (
                                            <span title={`recorte #${sib.session_id}`}>
                                                parte {sib.index} ({sib.transcription_id ? 'fora do período/filtro' : sib.status})
                                            </span>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const readOnly = !!detail?.is_reviewed;
    const hasEval = ((detail?.result?.data?.evaluation_details) || []).length > 0;
    const renderDetail = () => (
        <div className="rp-detail">
            <div className="rp-detail-head">
                <div className="rp-detail-metrics">
                    {hasEval ? (
                        <>
                            Nota efetiva: <strong>{num(detail.effective_metrics?.final_score)}</strong>
                            {' · '}Pontos (perguntas): <strong>{intNum(detail.effective_metrics?.earned_points)}</strong>
                            {detail.model && <span className="rp-data-model"> · 🤖 {detail.model}</span>}
                            {detail.effective_metrics?.is_revised ? ' · ✏️ revisado' : ''}
                        </>
                    ) : (
                        <span className="rp-empty">Atendimento sem avaliação de perguntas.</span>
                    )}
                    {readOnly && <span className="rp-badge-reviewed"> ✓ Revisado{detail.reviewed_by ? ` por ${detail.reviewed_by}` : ''}</span>}
                </div>
                <div className="rp-detail-actions">
                    <button className="rp-btn rp-btn-sm" onClick={loadTranscript}>📄 Transcrição</button>
                    {!restrictedAdmin && attendances.find((x) => x.transcription_id === detail.transcription_id)?.awon_session_id && (
                        <button className="rp-btn rp-btn-sm" onClick={loadComparacao} aria-pressed={!!comparacao}
                            title="O que o julgador tirou e acrescentou em relação aos recortes originais (só para consulta)">
                            🔀 Original × julgador
                        </button>
                    )}
                    {detail.has_audio && !restrictedAdmin && (
                        <button className="rp-btn rp-btn-sm" onClick={() => loadAudio(false)}>🔊 Áudio</button>
                    )}
                    {detail.has_audio && (
                        <button className="rp-btn rp-btn-sm" onClick={() => loadAudio(true)}
                            title="Áudio com os trechos fora do atendimento silenciados">
                            🔇 Áudio Anonimizado
                        </button>
                    )}
                    <button className="rp-btn rp-btn-sm" onClick={() => openDataModal(detail.transcription_id, detail.feedback_id)}>🗂️ Dados</button>
                    <button className="rp-btn rp-btn-sm"
                        onClick={() => setConfirmModal({ message: 'Ao marcar como revisado, não será mais possível editar a revisão deste atendimento. Deseja continuar?', onConfirm: markReviewed })}
                        disabled={readOnly || marking}>
                        {readOnly ? '✓ Revisado' : (marking ? 'Marcando…' : '✅ Marcar revisão')}
                    </button>
                    <button className="rp-btn rp-btn-sm" onClick={() => setDetail(null)}>✕ Fechar</button>
                </div>
            </div>

            {renderAwonOrigem(attendances.find((x) => x.transcription_id === detail.transcription_id))}

            {(detail.feedback_id || detail.feedback_likes != null || detail.feedback_comments || detail.feedback_comments_answer) && (
                <div className={`rp-fb-box ${detail.feedback_likes === -1 ? 'rp-fb-dislike' : (detail.feedback_likes === 1 ? 'rp-fb-like' : '')}`}>
                    <div className="rp-fb-box-head">
                        Feedback do vendedor:{' '}
                        <strong>
                            {detail.feedback_likes === -1 ? '👎 Dislike' : (detail.feedback_likes === 1 ? '👍 Like' : '— sem avaliação —')}
                        </strong>
                    </div>
                    {detail.feedback_comments && (
                        <p className="rp-fb-box-comment"><span>Comentário:</span> {String(detail.feedback_comments).trim()}</p>
                    )}
                    {detail.feedback_id && (
                        <div className="rp-fb-answer">
                            <label>Resposta ao vendedor</label>
                            <textarea value={fbAnswer} rows={2} placeholder="Escreva a resposta ao vendedor…"
                                onChange={(e) => setFbAnswer(e.target.value)} />
                            <div className="rp-fb-answer-actions">
                                <button className="rp-btn rp-btn-sm rp-btn-primary" onClick={saveFeedbackAnswer}
                                    disabled={fbSaving || fbAnswer === (detail.feedback_comments_answer || '')}>
                                    {fbSaving ? 'Salvando…' : 'Salvar resposta'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {transcript !== null && <TranscricaoDestacada texto={transcript} />}
            {comparacao && <ComparacaoJulgador dados={comparacao} />}
            {contextTranscript !== null && (
                <>
                    <div className="rp-subtitle" style={{ marginTop: '12px', fontWeight: 600 }}>
                        Transcrição com Contexto Anonimizado
                    </div>
                    <TranscricaoDestacada texto={contextTranscript} />
                    {contextSpans.length > 0 && (
                        <details style={{ marginTop: '8px' }} open>
                            <summary style={{ cursor: 'pointer' }}>
                                O que foi removido, e por quê ({contextSpans.length})
                            </summary>
                            <ul style={{ margin: '8px 0 0 0', paddingLeft: '18px' }}>
                                {contextSpans.map((sp) => (
                                    <li key={sp.i} style={{ marginBottom: '4px' }}>
                                        <code>{sp.ts}</code> <em>{sp.reason}</em>
                                        {sp.text ? <> — {sp.text}</> : null}
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </>
            )}
            {audioUrl && <audio className="rp-audio" controls src={audioUrl}>seu navegador não suporta áudio</audio>}

            {hasEval && <div className="rp-questions">
                {ordenarPerguntas(
                    detail.result?.data?.evaluation_details || [],
                    detail.feedback_likes === -1 ? comportamentosDoDislike(detail.feedback_comments) : [],
                ).map((item, idx) => {
                    const d = drafts[item.item] || {};
                    const conf = item.score_prompt?.confidence;
                    const modified = isModified(item);
                    const aberta = !!abertas[item.item];
                    const bkMeta = item.behavior_key ? (detail.behaviors || {})[item.behavior_key] : null;
                    const pts = d.earn === true && d.cond !== false && d.score != null && d.points != null
                        ? Number(d.points) * Number(d.score) : 0;
                    // cor suave pela nota que vale (a revisada, se mudou): 1 = verde, 0 = amarelo; n/a ou sem nota = neutra
                    const notaCls = d.cond === false || d.score == null ? ''
                        : Number(d.score) === 1 ? 'rp-q-nota1' : Number(d.score) === 0 ? 'rp-q-nota0' : '';
                    return (
                        <div key={item.item} className={`rp-q ${notaCls} ${modified ? 'rp-q-modified' : ''} ${aberta ? '' : 'rp-q-collapsed'}`}>
                            <div className="rp-q-summary" role="button" tabIndex={0} aria-expanded={aberta}
                                onClick={() => setAbertas((a) => (a[item.item] ? {} : { [item.item]: true }))}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAbertas((a) => (a[item.item] ? {} : { [item.item]: true })); } }}>
                                <span className="rp-q-chevron">{aberta ? '▾' : '▸'}</span>
                                <span className="rp-q-num">{idx + 1}.</span>
                                <span className="rp-q-summary-text">{item.question}</span>
                                {item.behavior_key && (
                                    <span className="rp-q-summary-beh" style={bkMeta?.color ? { background: bkMeta.color, color: '#fff' } : undefined}>
                                        {bkMeta?.icon ? bkMeta.icon + ' ' : ''}{bkMeta?.display_name || item.behavior_key}
                                    </span>
                                )}
                                <span className="rp-q-summary-score">
                                    {d.cond === false ? 'n/a' : `nota ${d.score ?? '—'}`}{pts ? ` · ${intNum(pts)} pts` : ''}
                                </span>
                                {modified && <span className="rp-q-summary-mod" title="alterada nesta revisão">✏️</span>}
                            </div>
                            {aberta && <>
                            <div className="rp-q-head">
                                <span className="rp-q-cat">{item.category}</span>
                                <span className="rp-q-weight">peso {item.weight}</span>
                                {conf !== null && conf !== undefined && <span className="rp-q-conf">confiança {conf}</span>}
                                {(() => {
                                    const bk = item.behavior_key;
                                    const meta = bk ? (detail.behaviors || {})[bk] : null;
                                    const label = meta?.display_name || bk;
                                    return (
                                        <span className="rp-q-behavior" style={meta?.color ? { background: meta.color, color: '#fff', padding: '1px 8px', borderRadius: 10, fontSize: '0.72rem' } : { fontSize: '0.72rem', color: 'var(--text-secondary,#888)' }}>
                                            {label ? `${meta?.icon ? meta.icon + ' ' : ''}${label}` : '— sem comportamento —'}
                                        </span>
                                    );
                                })()}
                            </div>
                            {item.condition_text && <div className="rp-q-cond-text">{item.condition_text}</div>}
                            <div className="rp-q-controls">
                                <label className="rp-q-cond">
                                    <input type="checkbox" checked={d.cond !== false} disabled={readOnly}
                                        onChange={(e) => setDraft(item.item, { cond: e.target.checked, ...(e.target.checked ? {} : { score: null }) })} />
                                    Aplicável
                                </label>
                                <label className="rp-q-score">
                                    Nota
                                    <input type="number" min="0" max="10" step="1"
                                        value={d.cond === false ? '' : (d.score ?? '')}
                                        disabled={readOnly || d.cond === false}
                                        onChange={(e) => setDraft(item.item, { score: e.target.value === '' ? null : Number(e.target.value) })} />
                                </label>
                                <label className="rp-q-cond">
                                    <input type="checkbox" checked={d.earn === true} disabled={readOnly}
                                        onChange={(e) => setDraft(item.item, { earn: e.target.checked })} />
                                    Pontua
                                </label>
                                <label className="rp-q-score">
                                    Pontos
                                    <input type="number" min="0" step="1"
                                        value={d.points ?? ''}
                                        disabled={readOnly || d.earn !== true}
                                        onChange={(e) => setDraft(item.item, { points: e.target.value === '' ? null : Number(e.target.value) })} />
                                </label>
                                <span className="rp-q-earned">
                                    = {intNum(d.earn === true && d.cond !== false && d.score != null && d.points != null ? Number(d.points) * Number(d.score) : 0)} pts
                                </span>
                            </div>
                            <div className="rp-q-evidence">
                                <label>Evidência</label>
                                <textarea value={d.evidence ?? ''} disabled={readOnly} onChange={(e) => setDraft(item.item, { evidence: e.target.value })} rows={2} />
                            </div>
                            {(() => {
                                // Detalhe do comportamento detectado (prompt de detalhe do comportamento): o que foi
                                // oferecido, como, se o cliente aceitou... Campos livres por comportamento.
                                const det = item.behavior_key ? (detail.behavior_details || {})[item.behavior_key] : null;
                                const occs = det?.ocorrencias || [];
                                if (!occs.length) return null;
                                const LABELS = { item_oferecido: 'Item oferecido', categoria: 'Categoria', produto_principal: 'Com o produto',
                                    como_foi_oferecido: 'Como', argumento: 'Argumento', preco_ou_desconto: 'Preço / desconto',
                                    cliente_aceitou: 'Cliente aceitou', houve_objecao: 'Houve objeção', objecao: 'Objeção',
                                    objecao_tipo: 'Tipo de objeção', contorno: 'Como contornou', contorno_tipo: 'Tipo de contorno',
                                    instante: 'Instante', cliente: 'Cliente' };
                                const fmt = (v) => (Array.isArray(v) ? v.join(', ') : (v === null || v === undefined || v === '' ? '—' : String(v)));
                                return (
                                    <div className="rp-q-details">
                                        <label>🔎 Detalhe do comportamento{occs.length > 1 ? ` (${occs.length} ocorrências)` : ''}</label>
                                        {occs.map((o, i) => (
                                            <div key={i} className="rp-q-detail-occ">
                                                {Object.entries(o).filter(([k]) => k !== 'trecho').map(([k, v]) => (
                                                    <div key={k} className="rp-q-detail-row">
                                                        <span className="rp-q-detail-key">{LABELS[k] || k.replace(/_/g, ' ')}</span>
                                                        <span className="rp-q-detail-val">{fmt(v)}</span>
                                                    </div>
                                                ))}
                                                {o.trecho && <div className="rp-q-detail-quote">“{o.trecho}”</div>}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })()}
                            {(() => {
                                // Justificativa do autor. Aparece em todo item quando o atendimento está
                                // revisado (por item OU marcado como revisado), mesmo sem texto.
                                const rev = detail.effective_by_item?.[item.item];
                                if (!rev && !detail.is_reviewed) return null;
                                const just = ((rev && rev.justification) || '').trim();
                                const autor = (rev && rev.reviewer_name) || detail.reviewed_by;
                                return (
                                    <div className="rp-q-review-note">
                                        <label>💬 Justificativa da revisão{autor ? ` — ${autor}` : ''}{rev && rev.created ? ` · ${brDateTime(rev.created)}` : ''}</label>
                                        <p className={just ? '' : 'rp-empty'}>{just || '— sem justificativa —'}</p>
                                    </div>
                                );
                            })()}
                            {modified && !readOnly && (
                                <div className="rp-q-just">
                                    <label>Justificativa da revisão</label>
                                    <input type="text" value={d.justification ?? ''} placeholder="Motivo da alteração"
                                        onChange={(e) => setDraft(item.item, { justification: e.target.value })} />
                                </div>
                            )}
                            </>}
                        </div>
                    );
                })}
            </div>}

            {hasEval && <div className="rp-detail-foot">
                {readOnly
                    ? <span>Atendimento revisado — edição de pontuação bloqueada.</span>
                    : <span>{modifiedItems.length} pergunta(s) alterada(s)</span>}
                <button className="rp-btn rp-btn-primary"
                    onClick={() => setConfirmModal({ message: 'Após salvar, a revisão deste atendimento será marcada como revisada e não poderá mais ser editada. Deseja continuar?', onConfirm: saveReview })}
                    disabled={readOnly || savingReview || modifiedItems.length === 0}>
                    {savingReview ? 'Salvando…' : 'Salvar revisão'}
                </button>
            </div>}
        </div>
    );

    const totals = results?.totals;
    // Resumo dos cards, somando os vendedores pelo MESMO helper das colunas (`efetivo`). Usar o
    // `totals` da API aqui faria card e coluna divergirem: `totals` é rollup do ongoing e não
    // enxerga fase com régua própria. Horas seguem vindo de `totals` — a fase não reporta hora.
    const resumo = (results?.by_salesman || []).reduce((acc, s) => {
        const e = efetivo(s);
        acc.validos.count += e.validos.count;   acc.validos.points += e.validos.points;
        acc.invalidos.count += e.invalidos.count; acc.invalidos.points += e.invalidos.points;
        acc.total += e.total;
        acc.temFase = acc.temFase || e.temFase;
        return acc;
    }, { validos: { count: 0, points: 0 }, invalidos: { count: 0, points: 0 }, total: 0, temFase: false });
    // Autores de revisão presentes nos atendimentos carregados (para o filtro "Revisor").
    const reviewers = [...new Set(attendances.map((a) => a.reviewed_by).filter(Boolean))].sort();

    return (
        <div className="rp-page">
            {toast && <div className={`rp-toast rp-toast-${toast.type}`}>{toast.msg}</div>}

            {virtualModal && (
                <div className="rp-modal-overlay" onClick={(e) => e.target === e.currentTarget && closeVirtualModal()}>
                    <div className="rp-modal">
                        <div className="rp-modal-header">
                            <h3>Incluir Atendimento Virtual</h3>
                            <button className="rp-modal-close" onClick={closeVirtualModal}>×</button>
                        </div>
                        <div className="rp-modal-body">
                            <p className="rp-modal-sub">
                                Vendedor: <strong>{virtualModal.s.salesman_name}</strong><FaseBadge fase={fasesVend[String(virtualModal.s.salesman_id)]} /> · Loja: {virtualModal.s.store_name}
                            </p>
                            <div className="rp-field">
                                <label>Data/hora de início * (Brasil)</label>
                                <input type="datetime-local" value={vaForm.date_start}
                                    onChange={(e) => setVaForm((f) => ({ ...f, date_start: e.target.value }))} />
                            </div>
                            <div className="rp-field">
                                <label>Data/hora de fim * (Brasil)</label>
                                <input type="datetime-local" value={vaForm.date_end}
                                    onChange={(e) => setVaForm((f) => ({ ...f, date_end: e.target.value }))} />
                            </div>
                            <div className="rp-field">
                                <label>Classificação *</label>
                                <select value={vaForm.classification_id}
                                    onChange={(e) => setVaForm((f) => ({ ...f, classification_id: e.target.value }))}>
                                    <option value="">— Selecione —</option>
                                    {vaClassifications.map((c) => (
                                        <option key={c.id} value={c.id}>{c.classification}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="rp-modal-footer">
                            <button className="rp-btn" onClick={closeVirtualModal} disabled={vaSaving}>Cancelar</button>
                            <button className="rp-btn rp-btn-primary" onClick={saveVirtualAttendance} disabled={vaSaving}>
                                {vaSaving ? 'Salvando…' : 'Criar atendimento'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {dataModal && (
                <div className="rp-modal-overlay" onClick={(e) => e.target === e.currentTarget && setDataModal(null)}>
                    <div className="rp-modal rp-modal-wide">
                        <div className="rp-modal-header">
                            <h3>Dados do atendimento{dataModal.feedback_id ? ` #${dataModal.feedback_id}` : ''}</h3>
                            <button className="rp-modal-close" onClick={() => setDataModal(null)}>×</button>
                        </div>
                        <div className="rp-modal-body">
                            {dataLoading && <p>Carregando…</p>}
                            {!dataLoading && dataModal.data && Object.keys(CHILD_LABELS).map((child) => {
                                const val = dataModal.data[child];
                                const has = val !== null && val !== undefined;
                                const model = (dataModal.models || {})[child];
                                return (
                                    <details key={child} className="rp-data-section" open={has}>
                                        <summary>
                                            {CHILD_LABELS[child]}
                                            {model && <span className="rp-data-model"> · 🤖 {model}</span>}
                                            {!has && <span className="rp-empty"> — sem dados</span>}
                                        </summary>
                                        {has && renderChildContent(child, val)}
                                    </details>
                                );
                            })}
                            {!dataLoading && dataModal.data && !Object.keys(CHILD_LABELS).some((c) => dataModal.data[c] != null) && (
                                <p className="rp-empty">Nenhuma extração disponível para este atendimento.</p>
                            )}
                        </div>
                        <div className="rp-modal-footer">
                            <button className="rp-btn" onClick={() => setDataModal(null)}>Fechar</button>
                        </div>
                    </div>
                </div>
            )}

            {confirmModal && (
                <div className="rp-modal-overlay" onClick={(e) => e.target === e.currentTarget && setConfirmModal(null)}>
                    <div className="rp-modal">
                        <div className="rp-modal-header">
                            <h3>Confirmar</h3>
                            <button className="rp-modal-close" onClick={() => setConfirmModal(null)}>×</button>
                        </div>
                        <div className="rp-modal-body">
                            <p className="rp-confirm-msg">{confirmModal.message}</p>
                        </div>
                        <div className="rp-modal-footer">
                            <button className="rp-btn" onClick={() => setConfirmModal(null)}>Não</button>
                            <button className="rp-btn rp-btn-primary" onClick={async () => { const fn = confirmModal.onConfirm; setConfirmModal(null); if (fn) await fn(); }}>Sim, continuar</button>
                        </div>
                    </div>
                </div>
            )}

            <h1 className="rp-title">⭐ Revisão de Pontos</h1>

            {/* Filtros */}
            <div className="rp-filters">
                <div className="rp-field">
                    <label>Empresa</label>
                    <select value={subdomain} onChange={(e) => setSubdomain(e.target.value)}>
                        <option value="">— Selecione —</option>
                        {companies.map((c) => <option key={c.subdomain} value={c.subdomain}>{c.name}</option>)}
                    </select>
                </div>
                <div className="rp-field">
                    <label>Loja</label>
                    <select value={storeId} onChange={(e) => { setStoreId(e.target.value); setSalesmanId(''); }} disabled={!subdomain}>
                        <option value="">Todas</option>
                        {stores.map((s) => <option key={s.id} value={s.id}>{s.display_name || s.name}</option>)}
                    </select>
                </div>
                <div className="rp-field">
                    <label>Vendedor</label>
                    <select value={salesmanId} onChange={(e) => setSalesmanId(e.target.value)} disabled={!subdomain}>
                        <option value="">{salesmen.length ? 'Todos' : (subdomain ? 'Carregando…' : 'Todos')}</option>
                        {salesmen.map((s) => (
                            <option key={s.salesman_id} value={s.salesman_id}>
                                {s.salesman_name}{s.is_active ? '' : ' (inativo)'}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="rp-field">
                    <label>Revisão</label>
                    <select value={reviewedFilter} onChange={(e) => setReviewedFilter(e.target.value)}>
                        <option value="all">Todos</option>
                        <option value="reviewed">Somente revisados</option>
                        <option value="not_reviewed">Somente não revisados</option>
                    </select>
                </div>
                <div className="rp-field">
                    <label>Revisor</label>
                    <select value={reviewerFilter} onChange={(e) => setReviewerFilter(e.target.value)}>
                        <option value="">Todos</option>
                        {reviewers.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>
                <div className="rp-field">
                    <label>Início</label>
                    <select value={startIdx} onChange={(e) => setStartIdx(+e.target.value)}>
                        {ALL_DATES.map((d, i) => <option key={i} value={i}>{d}</option>)}
                    </select>
                </div>
                <div className="rp-field">
                    <label>Fim</label>
                    <select value={endIdx} onChange={(e) => setEndIdx(+e.target.value)}>
                        {ALL_DATES.map((d, i) => <option key={i} value={i} disabled={i < startIdx}>{d}</option>)}
                    </select>
                </div>
                <div className="rp-field rp-presets">
                    <label>Atalhos</label>
                    <div className="rp-preset-btns">
                        {PRESETS.map((p, i) => (
                            <button key={i} type="button"
                                className={`rp-pill${startIdx === p.s && endIdx === p.e ? ' active' : ''}`}
                                onClick={() => { setStartIdx(p.s); setEndIdx(p.e); }}>
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="rp-filters-actions">
                    <button className="rp-btn rp-btn-primary" onClick={buscar} disabled={loading || !subdomain}>
                        {loading ? 'Buscando…' : 'Buscar'}
                    </button>
                </div>
            </div>

            {/* Resumo de pontos */}
            {totals && (
                <div className="rp-summary">
                    {/* Os cards somam os vendedores pelo MESMO helper das colunas. Somar aqui em vez
                        de usar `totals` da API é o que impede card e coluna de divergirem: `totals`
                        é rollup do ongoing e não enxerga fase com régua própria. Horas continuam
                        vindo de `totals` — a fase não reporta hora. */}
                    <div className="rp-cards">
                        <div className="rp-card" title={(totals.by_behavior || []).map(b => `${b.display_name}: ${intNum(b.points)}`).join('\n') || 'sem comportamentos'}>
                            <span>Pontos: Comportamentos</span><strong>{intNum(sumBeh(totals.by_behavior))}</strong>
                            <em>{(totals.by_behavior || []).length} comportamento(s)</em></div>
                        <div className="rp-card"><span>Pontos: Atend. válidos</span><strong>{intNum(resumo.validos.points)}</strong>
                            <em>{resumo.validos.count} atend. × {num(totals.by_valid_attendances.points_per_unit)} pts{resumo.temFase ? ' · inclui fase' : ''}</em></div>
                        <div className="rp-card"><span>Pontos: Atend. inválidos</span><strong>{intNum(resumo.invalidos.points)}</strong>
                            <em>{resumo.invalidos.count} atend. × {num(totals.by_invalid_attendances.points_per_unit)} pts{resumo.temFase ? ' · inclui fase' : ''}</em></div>
                        <div className="rp-card"><span>Pontos: Horas gravação</span><strong>{intNum(totals.by_recording_hours.points)}</strong>
                            <em>{num(totals.by_recording_hours.hours)}h × {num(totals.by_recording_hours.points_per_unit)} pts</em></div>
                        <div className="rp-card rp-card-total"><span>Pontuação total</span><strong>{intNum(resumo.total)}</strong></div>
                    </div>
                </div>
            )}

            {/* Vendedores → atendimentos → perguntas (drill-down) */}
            {results && (
                <div className="rp-attendances">
                    <h2>Vendedores ({results.by_salesman?.length || 0})</h2>
                    {feedbackFilter && (
                        <div className="rp-fb-filter">
                            🔎 Filtrando a lista de atendimentos por {feedbackFilter.size} feedback(s) do alerta.
                            <button className="rp-btn rp-btn-sm" onClick={() => setFeedbackFilter(null)}>limpar filtro</button>
                        </div>
                    )}
                    {(!results.by_salesman || results.by_salesman.length === 0) && (
                        <p className="rp-empty">Nenhum vendedor com pontos no período/filtro.</p>
                    )}
                    {results.by_salesman?.length > 0 && (
                        <table className="rp-table">
                            <thead><tr>
                                <th>Vendedor</th><th>Ativo</th><th>Loja</th><th>Comportamentos</th><th>Válidos</th>
                                <th>Inválidos</th><th>Horas</th><th>Total</th><th>Meta diária</th><th>Vend. (meta)</th><th></th><th></th>
                            </tr></thead>
                            <tbody>
                                {results.by_salesman.map((s) => {
                                    const skey = `${s.store_id}-${s.salesman_id}`;
                                    const sopen = openSalesman === skey;
                                    let smAtt = attendances.filter(
                                        (a) => a.salesman_id === s.salesman_id && a.store_id === s.store_id
                                    );
                                    if (feedbackFilter) smAtt = smAtt.filter((a) => feedbackFilter.has(a.feedback_id));
                                    if (reviewedFilter === 'reviewed') smAtt = smAtt.filter((a) => a.is_reviewed);
                                    else if (reviewedFilter === 'not_reviewed') smAtt = smAtt.filter((a) => !a.is_reviewed);
                                    if (reviewerFilter) smAtt = smAtt.filter((a) => a.reviewed_by === reviewerFilter);
                                    // Com filtro de revisão/revisor ativo, esconde vendedores sem atendimentos que casem.
                                    const filterActive = !!feedbackFilter || reviewedFilter !== 'all' || !!reviewerFilter;
                                    if (filterActive && smAtt.length === 0) return null;
                                    const g = goalInfo(s.store_id);
                                    // Colunas mostram o EFETIVO (ongoing + fase fora do placar). Ver efetivo().
                                    const ef = efetivo(s);
                                    const tipFase = (m) => ef.temFase
                                        ? ef.fases.map((f) => `${f.phase_name}: ${intNum((f[m] || {}).points)} pts (${(f[m] || {}).count ?? 0} atend.)`).join('\n')
                                          + '\n\nA fase tem régua de pontuação própria: estes pontos entram aqui, mas não no ranking.'
                                        : undefined;
                                    return (
                                        <React.Fragment key={skey}>
                                            <tr className={sopen ? 'rp-row-active' : ''}>
                                                <td>
                                                    {s.salesman_name}<FaseBadge fase={fasesVend[String(s.salesman_id)]} />
                                                    <PontosForaDoPlacar byPhase={s.by_phase} />
                                                </td>
                                                <td>{s.is_active ? '✅ Ativo' : '🚫 Inativo'}</td>
                                                <td>{s.store_name}</td>
                                                <td title={dicaComportamentos(s, ef)}>
                                                    {intNum(sumBeh(s.by_behavior) + ef.comportamento)}
                                                    <span className="rp-de-fase" title="ver composição">🔎</span>
                                                    {ef.comportamento > 0 && <span className="rp-de-fase" title="inclui pontos da fase">🎯</span>}
                                                    {g && <small> · meta {num(g.questions)}</small>}
                                                </td>
                                                <td title={tipFase('by_valid_attendances')}>{intNum(ef.validos.points)} <small>({ef.validos.count})</small>{ef.validos.temFase && <span className="rp-de-fase" title="inclui pontos da fase">🎯</span>}{g && <small> · meta {num(g.valid_attendance)}</small>}</td>
                                                <td title={tipFase('by_invalid_attendances')}>{intNum(ef.invalidos.points)} <small>({ef.invalidos.count})</small>{ef.invalidos.temFase && <span className="rp-de-fase" title="inclui pontos da fase">🎯</span>}{g && <small> · meta {num(g.invalid_attendance)}</small>}</td>
                                                <td>{intNum(s.by_recording_hours.points)} <small>({num(s.by_recording_hours.hours)}h)</small>{g && <small> · meta {num(g.recording_hours)}</small>}</td>
                                                <td><strong>{intNum(ef.total)}</strong>{ef.temFase && <span className="rp-de-fase" title="inclui pontos da fase">🎯</span>}</td>
                                                <td>{g ? num(g.daily_goal) : '—'}</td>
                                                <td>{g ? g.salesman_count : '—'}</td>
                                                <td><button className="rp-btn rp-btn-sm" onClick={() => toggleSalesman(skey)}>
                                                    {sopen ? '▲ Fechar' : `▼ Revisar (${smAtt.length})`}
                                                </button></td>
                                                <td className="rp-cell-right"><button className="rp-btn rp-btn-sm" onClick={() => openVirtualModal(s)}>
                                                    ➕ Atendimento Virtual
                                                </button></td>
                                            </tr>
                                            {sopen && (
                                                <tr className="rp-detail-row">
                                                    <td colSpan={12}>
                                                        {smAtt.length === 0 ? (
                                                            <p className="rp-empty">Nenhum atendimento deste vendedor no período.</p>
                                                        ) : (
                                                            <table className="rp-table rp-subtable">
                                                                <thead><tr>
                                                                    <th>Data/hora</th><th>Origem</th><th>ID (feedback_id)</th><th>Classificação</th><th>Classif. original</th><th>Perguntas</th>
                                                                    <th>Nota orig.→efet.</th><th>Pontos</th><th></th>
                                                                </tr></thead>
                                                                <tbody>
                                                                    {smAtt.map((a) => {
                                                                        const opening = openingId === a.transcription_id;
                                                                        const open = opening || detail?.transcription_id === a.transcription_id;
                                                                        return (
                                                                            <React.Fragment key={a.transcription_id}>
                                                                                <tr id={`rp-att-${a.transcription_id}`} className={open ? 'rp-row-active' : ''}>
                                                                                    <td title={a.recorded_at && a.created ? `gravado ${brDateTime(a.recorded_at)} · processado ${brDateTime(a.created)}` : undefined}>{brDateTime(a.recorded_at || a.created)}</td>
                                                                                    <td>{origemTag(a.origin)}{a.awon_split && (
                                                                                        <small className="rp-tag-split" title={`Parte ${a.awon_split.index} de ${a.awon_split.count} do recorte completo #${a.awon_split.parent}, dividido pela IA`}>
                                                                                            {' '}✂ {a.awon_split.index}/{a.awon_split.count}
                                                                                        </small>
                                                                                    )}</td>
                                                                                    <td>{a.feedback_id ?? '—'}{a.is_virtual ? <small className="rp-tag-virtual"> 🧪 virtual</small> : ''}{a.is_reviewed ? <small className="rp-badge-reviewed"> ✓ Revisado{a.reviewed_by ? ` por ${a.reviewed_by}` : ''}</small> : ''}</td>
                                                                                    <td>{a.classification || '—'}</td>
                                                                                    <td>{a.original_classification || '—'}</td>
                                                                                    <td>{a.is_reviewable ? a.n_questions : '—'}</td>
                                                                                    <td>{a.is_reviewable ? <>{num(a.original_final_score)} → <strong>{num(a.effective_final_score)}</strong>{a.is_revised ? ' ✏️' : ''}</> : '—'}</td>
                                                                                    <td title={a.is_reviewable ? dicaPontosAtendimento(a) : undefined}>
                                                                                        {a.is_reviewable ? <>
                                                                                            <strong>{intNum(a.earned_points)}</strong>
                                                                                            <span className="rp-de-fase" title="ver composição">🔎</span>
                                                                                            {(a.questions || []).some((q) => (q.earned_points || 0) > 0 && !q.behavior_key) &&
                                                                                                <span className="rp-orfao" title="inclui comportamento fora do vocabulário da loja">⚠</span>}
                                                                                        </> : '—'}
                                                                                    </td>
                                                                                    <td>
                                                                                        <button className="rp-btn rp-btn-sm" onClick={() => toggleRevisar(a)}>
                                                                                            {open ? '▲ Fechar' : '▼ Revisar'}
                                                                                        </button>
                                                                                    </td>
                                                                                </tr>
                                                                                {open && (
                                                                                    <tr className="rp-detail-row">
                                                                                        <td colSpan={9}>
                                                                                            {opening ? <p>Carregando atendimento…</p> : (detail && renderDetail())}
                                                                                        </td>
                                                                                    </tr>
                                                                                )}
                                                                            </React.Fragment>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
}

export default RevisaoPontos;
